import React from 'react';
import { Card } from 'react-bootstrap';
import { EChartsOption } from 'echarts-for-react';
import EChartsReact from 'echarts-for-react';

interface MarketTrendChartProps {
  data: {
    months: string[];
    median_prices: number[];
    prediction_value: number;
  };
  title?: string;
}

interface FeatureImportanceChartProps {
  features: {
    name: string;
    importance: number;
    value: string | number;
  }[];
  title?: string;
}

interface PriceDistributionChartProps {
  data: {
    price_ranges: string[];
    counts: number[];
    prediction_value: number;
  };
  title?: string;
}

export const MarketTrendChart: React.FC<MarketTrendChartProps> = ({ 
  data, 
  title = "Market Price Trend" 
}) => {
  const option: EChartsOption = {
    title: {
      text: title,
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        let result = `${params[0].axisValue}<br/>`;
        params.forEach((param: any) => {
          result += `${param.seriesName}: $${param.value.toLocaleString()}<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: ['Market Median', 'Your Prediction'],
      top: 30
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: data.months
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '${value}'
      }
    },
    series: [
      {
        name: 'Market Median',
        type: 'line',
        smooth: true,
        itemStyle: {
          color: '#5470c6'
        },
        data: data.median_prices
      },
      {
        name: 'Your Prediction',
        type: 'line',
        smooth: true,
        itemStyle: {
          color: '#91cc75'
        },
        lineStyle: {
          type: 'dashed'
        },
        data: data.months.map(() => data.prediction_value)
      }
    ]
  };

  return (
    <Card className="h-100">
      <Card.Body>
        <EChartsReact 
          option={option} 
          style={{ height: '300px' }}
          opts={{ renderer: 'canvas' }}
        />
      </Card.Body>
    </Card>
  );
};

export const FeatureImportanceChart: React.FC<FeatureImportanceChartProps> = ({ 
  features, 
  title = "Price Prediction Factors" 
}) => {
  const option: EChartsOption = {
    title: {
      text: title,
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: function(params: any) {
        const param = params[0];
        const feature = features[param.dataIndex];
        return `${feature.name}<br/>
                Impact: ${(param.value * 100).toFixed(1)}%<br/>
                Value: ${feature.value}`;
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '12%',
      containLabel: true
    },
    xAxis: {
      type: 'value',
      max: 1,
      axisLabel: {
        formatter: '{value}%'
      }
    },
    yAxis: {
      type: 'category',
      data: features.map(f => f.name),
      inverse: true
    },
    series: [
      {
        type: 'bar',
        data: features.map(f => f.importance),
        itemStyle: {
          color: function(params: any) {
            const colors = ['#ee6666', '#5470c6', '#91cc75', '#fac858', '#73c0de'];
            return colors[params.dataIndex % colors.length];
          }
        },
        barWidth: '60%'
      }
    ]
  };

  return (
    <Card className="h-100">
      <Card.Body>
        <EChartsReact 
          option={option} 
          style={{ height: '300px' }}
          opts={{ renderer: 'canvas' }}
        />
      </Card.Body>
    </Card>
  );
};

export const PriceDistributionChart: React.FC<PriceDistributionChartProps> = ({ 
  data, 
  title = "Market Price Distribution" 
}) => {
  const option: EChartsOption = {
    title: {
      text: title,
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '12%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: data.price_ranges,
      axisLabel: {
        rotate: 45
      }
    },
    yAxis: {
      type: 'value',
      name: 'Number of Properties'
    },
    series: [
      {
        type: 'bar',
        data: data.counts.map((count, index) => ({
          value: count,
          itemStyle: {
            color: data.price_ranges[index].includes('Your') ? '#91cc75' : '#5470c6'
          }
        })),
        barWidth: '60%'
      }
    ]
  };

  return (
    <Card className="h-100">
      <Card.Body>
        <EChartsReact 
          option={option} 
          style={{ height: '300px' }}
          opts={{ renderer: 'canvas' }}
        />
      </Card.Body>
    </Card>
  );
};

// Comprehensive chart component that combines multiple visualizations
interface SmartCMAChartsProps {
  predictionData: {
    value: number;
    confidence_score: number;
  };
  marketData?: {
    price_trends?: {
      months: string[];
      median_prices: number[];
    };
    feature_importance?: {
      name: string;
      importance: number;
      value: string | number;
    }[];
    price_distribution?: {
      price_ranges: string[];
      counts: number[];
    };
  };
}

export const SmartCMACharts: React.FC<SmartCMAChartsProps> = ({ 
  predictionData, 
  marketData 
}) => {
  if (!marketData) {
    return (
      <Card>
        <Card.Body className="text-center p-5">
          <p className="text-muted">
            Advanced charts will be available when market analysis data is included.
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="smart-cma-charts">
      {marketData.feature_importance && (
        <div className="mb-4">
          <FeatureImportanceChart features={marketData.feature_importance} />
        </div>
      )}
      
      <div className="row g-4">
        {marketData.price_trends && (
          <div className="col-lg-6">
            <MarketTrendChart 
              data={{
                ...marketData.price_trends,
                prediction_value: predictionData.value
              }}
            />
          </div>
        )}
        
        {marketData.price_distribution && (
          <div className="col-lg-6">
            <PriceDistributionChart 
              data={{
                ...marketData.price_distribution,
                prediction_value: predictionData.value
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SmartCMACharts; 